import { makeStyles } from '@material-ui/core/styles';
import React, { useState, useMemo } from 'react';
import Paper from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Paper';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import CircularProgress from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/CircularProgress';
import DeleteButton from 'Components/ComponentLibrary/Atoms/Buttons/DeleteButton';
import PropTypes from 'prop-types';

const useStyles = makeStyles(theme => ({
  inlineActionCard: {
    borderRadius: theme.radius.sm,
    padding: '26px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  media: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
  textContainer: {
    overflow: 'hidden',
    marginRight: 'auto',
  },
  title: {
    display: 'flex',
  },
  name: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  extension: {
    flexShrink: 0,
  },
  deleteButton: {
    flexShrink: 0,
    marginLeft: theme.spacing(3),
    height: 48, // matching the delete button height and width
    width: 48,
  },
}));

/**
 * Displays file information along with action item to delete the file.
 *
 * Splits the title to extract the file name prefix and the file extension suffix. The file name is truncated in
 * order to always have the file extension visible.
 *
 * @param media Left content
 * @param title File name
 * @param description Subtext
 * @param onDelete Delete action. If exists, will render a delete button
 */
export default function InlineFileActionCard({
  media,
  title,
  description,
  onDelete,
}) {
  const [deleting, setDeleting] = useState(false);

  const classes = useStyles();

  const [name, extension] = useMemo(() => [title.substring(0, title.lastIndexOf('.')), title.substring(title.lastIndexOf('.'))], [title]);

  async function handleDelete() {
    setDeleting(true);
    try {
      if (onDelete) {
        await Promise.resolve(onDelete());
      }
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Paper className={classes.inlineActionCard} variant={'outlined'}>
      {media ? <div className={classes.media}>{media}</div> : null}
      <div className={classes.textContainer}>
        <div className={classes.title}>
          <Typography className={classes.name} variant="h4">{name}</Typography>
          {extension ? <Typography className={classes.extension} variant="h4">{extension}</Typography> : null}
        </div>
        {description ? <Typography>{description}</Typography> : null}
      </div>
      {onDelete ? <div className={classes.deleteButton}>{deleting ? <CircularProgress color="disabled" /> : <DeleteButton onClick={handleDelete} />}</div> : null}
    </Paper>
  );
}
InlineFileActionCard.defaultProps = {
  media: undefined,
  description: undefined,
};
InlineFileActionCard.propTypes = {
  media: PropTypes.node,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  onDelete: PropTypes.func.isRequired,
};
